import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import MInfoCard from "@/components/ui/atoms/MInfoCard";
import styles from "../reminder.module.css";
import MTopBar from "@/components/ui/atoms/MTopBar";
import MHeader from "@/components/ui/organisms/MHeader";
import headerStyles from "@/components/ui/organisms/MHeader.module.css";
import { getMemberInfo } from "@/hooks/useMembers";
import MInputField from "@/components/ui/atoms/MInputField";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useReminders } from "@/hooks/useReminders";
import { CreateReminder, Reminders, ReminderType } from "@/types/reminders";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import {
  displayEventForField,
  paintEventDateField,
  paintHeader,
  paintReminderDateField,
  paintReminderTimeField,
} from "../remindersHelper";
import {
  getTimezone,
  initDate,
  inputFieldTime,
  updateTime,
} from "@/utils/date-utils";
import MFrame from "@/components/ui/atoms/MFrame";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { goBackOneLevel } from "@/utils/nav-utils";
import { useInsurance } from "@/hooks/useInsurance";
import { MAccordionHeader } from "@/components/ui/atoms/MAccordionHeader";
import { Insurance } from "@/types/insurance";
import { formatPrettyDate } from "@/utils/date-utils";
import { useTypeAhead } from "@/hooks/useTypeAhead";
import AutoCompleteInput from "@/components/ui/organisms/AutoCompleteInput";
import { isPast } from "date-fns";
import { IconShieldHeart } from "@tabler/icons-react";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

enum InsuranceStage {
  START,
  DETAILS,
  REMINDER_DETAILS,
  ADD,
  EDIT,
}

/**
 * Insurance Reminder Creation Page
 * Allows users to select an insurance type to create a reminder for
 */
const InsuranceReminder: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const reminder: Reminders = location.state?.reminder;
  const isEditing = !!reminder;
  const memberInfo = getMemberInfo();
  const [selectedInsurance, setSelectedInsurance] = useState<string | null>(
    reminder?.reminderFor ?? null
  );
  const [isCritical, setIsCritical] = useState<boolean>(
    reminder ? reminder.tier === "CRITICAL" : false
  );
  const { values: insurancePvtOptions } = useTypeAhead("insurance_private");
  const { values: insuranceGovOptions } = useTypeAhead("insurance_govt");

  const [showInsuranceAccordion, setShowInsuranceAccordion] =
    useState<boolean>(false);
  const [stage, setStage] = useState<InsuranceStage>(
    isEditing ? InsuranceStage.ADD : InsuranceStage.START
  );
  // Removed typeahead in favor of accordion with saved insurances
  const [expiryDate, setExpiryDate] = useState<Date | null>(
    initDate(reminder?.eventDate)
  );
  const [policyNumber, setPolicyNumber] = useState<string | null>(
    reminder?.reminderAttrs?.policyNumber ?? null
  );
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: boolean;
  }>({});
  const [showValidationErrors, setShowValidationErrors] =
    useState<boolean>(false);
  const [reminderDate, setReminderDate] = useState<Date | null>(
    initDate(reminder?.startDate)
  );
  const [reminderTime, setReminderTime] = useState<string | null>(
    inputFieldTime(reminder?.startDate, undefined, false, reminder?.timezone) ||
      null
  );
  const { addReminder, updateReminder, totalCount, fetchReminders } =
    useReminders(memberInfo.id);

  // Fetch reminder count on mount
  useEffect(() => {
    fetchReminders();
  }, []);
  const { fetched, insurances, fetchInsurance } = useInsurance({ memberInfo });
  const [isExistingInsurance, setIsExistingInsurance] =
    useState<boolean>(false);
  const [primaryHolder, setPrimaryHolder] = useState<string | null>(null);
  const [filteredInsuranceOptions, setFilteredInsuranceOptions] = useState<
    { name: string }[]
  >([]);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_INSURANCE,
    });
  }, []);

  useEffect(() => {
    if (!fetched) {
      fetchInsurance(true);
    }
  }, [fetched, fetchInsurance]);

  // Initialize filtered insurance options when typeahead data changes
  useEffect(() => {
    const allOptions = [
      ...insurancePvtOptions.map((name) => ({ name })),
      ...insuranceGovOptions.map((name) => ({ name })),
    ];
    setFilteredInsuranceOptions(allOptions);
  }, [insurancePvtOptions, insuranceGovOptions]);

  const handleInsuranceSelect = (insurance: Insurance) => {
    if (insurance) {
      setSelectedInsurance(getInsuranceDisplayName(insurance));
      setPolicyNumber(insurance.policyNumber);
      if (insurance.policyEndDate && !isPast(insurance.policyEndDate)) {
        setExpiryDate(new Date(insurance.policyEndDate));
      } else {
        setExpiryDate(null);
      }
      setIsExistingInsurance(true);
      setPrimaryHolder(insurance.primaryPolicyHolderName);
    }
  };

  const getInsuranceDisplayName = (insurance: Insurance) => {
    return insurance.insurerName || insurance.schemeName;
  };

  const handleBack = () => {
    if (stage === InsuranceStage.EDIT) {
      navigate(goBackOneLevel(location.pathname));
    }
    const nextStageVal = stage - 1;
    if (nextStageVal < InsuranceStage.START) {
      navigate(-1);
    } else {
      setStage(nextStageVal);
    }
  };

  const validateCurrentStage = (): boolean => {
    let isValid = true;
    const newErrors: { [key: string]: boolean } = {};

    if (stage === InsuranceStage.DETAILS) {
      // Validate provider is selected
      if (!selectedInsurance) {
        newErrors.provider = true;
        isValid = false;
      }

      // Validate expiry date is entered
      if (!expiryDate) {
        newErrors.expiryDate = true;
        isValid = false;
      }
    }

    setValidationErrors(newErrors);
    setShowValidationErrors(!isValid);
    return isValid;
  };

  const nextStage = () => {
    const nextStageVal = stage + 1;
    if (nextStageVal > InsuranceStage.ADD) {
      showToast({
        message: t("errors.maxStageReached"),
        variant: "error",
      });
    } else {
      if (validateCurrentStage()) {
        setStage(nextStageVal);

        // Track stage changes
        if (nextStageVal === InsuranceStage.DETAILS) {
          trackUserDataLoaded({
            memberInfo,
            screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
            screenName:
              ANALYTICS_SCREEN_NAMES.REMINDER_ADD_INSURANCE_INFORMATION,
          });
        } else if (nextStageVal === InsuranceStage.REMINDER_DETAILS) {
          trackUserDataLoaded({
            memberInfo,
            screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
            screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_INSURANCE_DATE,
          });
        } else if (nextStageVal === InsuranceStage.ADD) {
          trackUserDataLoaded({
            memberInfo,
            screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
            screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_INSURANCE_SUMMARY,
          });
        }

        setShowValidationErrors(false);
        // Auto-scroll to top when moving to next stage
        setTimeout(() => {
          document
            .querySelector("main")
            ?.scrollTo({ top: 0, behavior: "smooth" });
        }, 100);
      }
    }
  };

  const paintProviderInput = () => {
    // Filter options based on search query
    const handleSearch = (query: string) => {
      const allInsuranceOptions = [
        ...insurancePvtOptions.map((name) => ({ name })),
        ...insuranceGovOptions.map((name) => ({ name })),
      ];

      if (!query || query.trim() === "") {
        setFilteredInsuranceOptions(allInsuranceOptions);
      } else {
        const filtered = allInsuranceOptions.filter((option) =>
          option.name.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredInsuranceOptions(filtered);
      }
    };

    return (
      <AutoCompleteInput<{ name: string }>
        label={t("reminders.insurance.reminderForLabel")}
        data={filteredInsuranceOptions}
        getOptionLabel={(option) => option.name}
        onSelect={(item) => {
          if (typeof item === "string") {
            setSelectedInsurance(item);
          } else if (item && typeof item === "object" && "name" in item) {
            setSelectedInsurance(item.name);
          }
        }}
        placeholder={t("common.notAnswered")}
        value={selectedInsurance || ""}
        onChange={(v) => {
          setSelectedInsurance(v);
        }}
        onSearch={handleSearch}
        allowCustomInput={true}
        showAllOnFocus={true}
        className="w-full"
      />
    );
  };

  const paintInsuranceSelect = () => {
    if (insurances.length === 0) {
      return null;
    }
    return (
      <div className={styles.optionsContainer}>
        <MAccordionHeader
          title={t("reminders.insurance.selectFromList")}
          isOpen={showInsuranceAccordion}
          onClick={() => setShowInsuranceAccordion(!showInsuranceAccordion)}
          className="accordion-header-16"
          titleColor="var(--brand-colours-indigo-indigo-700, #45168C)"
        />
        {showInsuranceAccordion && (
          <div className={`${styles.optionsContainer}`}>
            {insurances.map((insurance) => {
              const displayName = getInsuranceDisplayName(insurance);
              const primaryHolder = insurance.primaryPolicyHolderName;
              const validUntil = insurance.isLifetimePolicy
                ? t("insurance.lifetime")
                : insurance.policyEndDate
                ? formatPrettyDate(new Date(insurance.policyEndDate))
                : null;

              return (
                <MInfoCard
                  key={insurance._id || insurance.id}
                  primaryLabel={displayName}
                  secondaryLabel={
                    primaryHolder
                      ? `Primary holder: ${primaryHolder}`
                      : undefined
                  }
                  tertiaryLabel={
                    validUntil ? `Valid until: ${validUntil}` : undefined
                  }
                  selected={selectedInsurance === displayName}
                  onSelect={() => handleInsuranceSelect(insurance)}
                  onClick={() => handleInsuranceSelect(insurance)}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const paintPolicyNumberInput = (
    disabled: boolean | undefined = undefined,
    isSummaryDisplay: boolean = false
  ) => {
    return (
      <MInputField
        label={t(
          `reminders.insurance.${
            isSummaryDisplay ? "final." : ""
          }policyNumberLabel`
        )}
        placeholder={t("common.notAnswered")}
        value={policyNumber}
        onChange={(e) => setPolicyNumber(e.target.value)}
        disabled={
          isExistingInsurance || (disabled === undefined ? false : disabled)
        }
        variant={isSummaryDisplay ? "SummaryDisplay" : "Underlined"}
      />
    );
  };

  const paintPolicyPrimaryHolder = (isSummaryDisplay: boolean = false) => {
    if (!isExistingInsurance) {
      return null;
    }
    return (
      <MInputField
        label={t("reminders.insurance.primaryHolderLabel")}
        placeholder={t("common.notAnswered")}
        value={primaryHolder}
        disabled={true}
        variant={isSummaryDisplay ? "SummaryDisplay" : "Underlined"}
      />
    );
  };

  // pass a function to check for disable state
  const paintNextButton = (disableCheck: () => boolean) => {
    return (
      <MButton
        variant="filled"
        size="lg"
        onClick={() => nextStage()}
        className={styles.nextButton}
        disabled={disableCheck()}
      >
        {t("common.next")}
      </MButton>
    );
  };

  const renderInsuranceDetails = () => {
    if (!selectedInsurance) return null;

    return (
      <div className="flex gap-3">
        <IconShieldHeart
          size={24}
          color="var(--brand-colours-indigo-indigo-500, #8764BB)"
        />
        <div className="flex flex-col gap-[6px]">
          <span
            className="font-semibold leading-[140%]"
            style={{ color: "#36116D" }}
          >
            {selectedInsurance}
          </span>
          {policyNumber && (
            <span className="text-xs font-medium" style={{ color: "#606060" }}>
              {policyNumber}
            </span>
          )}
          {primaryHolder && (
            <span className="text-xs font-bold" style={{ color: "#2A2A2A" }}>
              Primary holder: {primaryHolder}
            </span>
          )}
        </div>
      </div>
    );
  };

  const paintSummary = () => {
    const summary = [
      paintEventDateField(
        expiryDate,
        setExpiryDate,
        t,
        "insurance.expiryDate.final",
        true
      ),
      paintReminderDateField(
        reminderDate,
        setReminderDate,
        expiryDate,
        t,
        undefined,
        true,
        true
      ),
      paintReminderTimeField(
        reminderTime,
        setReminderTime,
        reminderDate,
        t,
        "add",
        undefined,
        true
      ),
    ];
    return summary;
  };

  const handleCreateReminder = async () => {
    // Check if this is first reminder BEFORE adding
    const isFirstReminder = totalCount === 0;

    const timezone = getTimezone();
    const newReminder: CreateReminder = {
      type: ReminderType.INSURANCE,
      startDate: updateTime(reminderDate, reminderTime),
      endDate: updateTime(reminderDate, reminderTime),
      reminderFor: selectedInsurance,
      eventDate: expiryDate,
      reminderAttrs: {
        policyNumber: policyNumber,
      },
      timezone,
      tier: isCritical ? "CRITICAL" : "ROUTINE",
    };

    if (isEditing) {
      await updateReminder(reminder.id, newReminder);
    } else {
      await addReminder(newReminder);
    }

    // Track reminder completion
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_INSURANCE_COMPLETE,
      ...(isFirstReminder && !isEditing && { action: "first_reminder_added" }),
    });

    navigate("/reminders");
  };

  const isFinalStage =
    stage === InsuranceStage.ADD || stage === InsuranceStage.EDIT;

  return (
    <div
      className={`flex flex-col min-h-screen relative ${
        isFinalStage ? "pb-[175px]" : "pb-[24px]"
      } ${isFinalStage ? styles.addStageBackgroundFinal : ""}`}
    >
      <MTopBar
        hideProfilePicker
        exitPath="/reminders"
        onBack={handleBack}
        withBackground={!isFinalStage}
        showPurpleGradient={isFinalStage}
      />

      <div
        className="profile-page-root flex flex-col"
        style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
      >
        <div className={styles.content}>
          {stage === InsuranceStage.START && (
            <div className={styles.cardContainer}>
              <div className="relative w-full h-full flex flex-col">
                {paintHeader(memberInfo, "reminders.insurance")}
                <div className="profile-input-container flex-1">
                  {paintProviderInput()}
                  {paintInsuranceSelect()}
                </div>
                {/* Next button at the bottom right - following Figma design */}
                <div className="confirm-footer pt-0">
                  {paintNextButton(() => !selectedInsurance)}
                </div>
              </div>
            </div>
          )}
          {stage === InsuranceStage.DETAILS && (
            <div className="relative w-full h-full flex flex-col">
              {paintHeader(memberInfo, "reminders.insurance.detailsPrompt")}
              <div className="profile-input-container flex-1">
                {paintPolicyNumberInput()}
                {paintPolicyPrimaryHolder()}
                {paintEventDateField(
                  expiryDate,
                  setExpiryDate,
                  t,
                  "insurance.expiryDate"
                )}
              </div>
              {/* Next button at the bottom right - following Figma design */}
              <div className="confirm-footer pt-0">
                {paintNextButton(() => !selectedInsurance || !expiryDate)}
              </div>
            </div>
          )}
          {stage === InsuranceStage.REMINDER_DETAILS && (
            <div className="relative w-full h-full flex flex-col">
              {paintHeader(memberInfo, "reminders.insurance.reminderDetails")}
              <div className="profile-input-container flex-1">
                {paintReminderDateField(
                  reminderDate,
                  setReminderDate,
                  expiryDate,
                  t,
                  "date"
                )}
                {paintReminderTimeField(
                  reminderTime,
                  setReminderTime,
                  reminderDate,
                  t
                )}
              </div>
              {/* Next button at the bottom right - following Figma design */}
              <div className="confirm-footer pt-0">
                {paintNextButton(() => !reminderDate || !reminderTime)}
              </div>
            </div>
          )}
          {[InsuranceStage.ADD, InsuranceStage.EDIT].includes(stage) && (
            <div className="w-full h-full flex flex-col">
              <MHeader
                module="reminders"
                memberInfo={memberInfo}
                title={t("reminders.insurance.addEditPrompt")}
                description={t("reminders.insurance.addEditDescription")}
                useParentGradient={false}
                className={
                  isFinalStage
                    ? headerStyles.stickyBelowTopBarWithGradient
                    : headerStyles.stickyBelowTopBar
                }
              />
              <div className="profile-input-container flex-1">
                <MFrame className="items-start py-6 px-5 mt-[20px] mb-[-22px] fixed left-[24px] right-[24px] bg-white z-10">
                  {renderInsuranceDetails()}
                </MFrame>
                <MFrame className="mt-[130px]">{paintSummary()}</MFrame>
              </div>
              <div className="confirm-footer">
                <MButton
                  variant="filled"
                  size="lg"
                  className="w-full"
                  onClick={() => handleCreateReminder()}
                >
                  {t("reminders.setReminder")}
                </MButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InsuranceReminder;
